import type { BulkAssignBody, BulkStatusBody, ManualCreateInput } from '@radar/contracts';
import { queryOptions } from '@tanstack/react-query';
import { api, call } from '@/lib/api';
import { type OpportunitiesListFilters, qk } from '@/lib/query-keys';
import type { OpportunitiesSearch } from '@/lib/search-schemas';

// Accès à `/api/v1/opportunities` et `/api/v1/users` (`api.md` § Opportunités, § Session). Toutes
// ces routes sont typées par `AppType` (T22) : aucun `fetch` brut ici.

export type OpportunitiesListData = Awaited<ReturnType<typeof listOpportunities>>;

/** Candidats de fusion : 10 opportunités du marché (`components.md` § MergeDialog). */
export const MERGE_SEARCH_PAGE_SIZE = 10;

/** Search params de `/opportunities` → query string de `GET /opportunities` (valeurs en chaînes). */
export function toOpportunitiesQuery(
  search: OpportunitiesSearch,
  marketId: string,
): OpportunitiesListFilters {
  return {
    page: String(search.page),
    pageSize: String(search.pageSize),
    sort: search.sort,
    order: search.order,
    marketId,
    ...(search.status && search.status.length > 0 ? { status: search.status } : {}),
    ...(search.minScore === undefined ? {} : { minScore: String(search.minScore) }),
    ...(search.maxScore === undefined ? {} : { maxScore: String(search.maxScore) }),
    ...(search.minConfidence === undefined ? {} : { minConfidence: String(search.minConfidence) }),
    ...(search.productType && search.productType.length > 0
      ? { productType: search.productType }
      : {}),
    ...(search.ownerId ? { ownerId: search.ownerId } : {}),
    ...(search.tags && search.tags.length > 0 ? { tags: search.tags } : {}),
    ...(search.q ? { q: search.q } : {}),
    ...(search.updatedSince ? { updatedSince: search.updatedSince } : {}),
  };
}

export function listOpportunities(query: OpportunitiesListFilters) {
  return call(api.opportunities.$get({ query }));
}

export const opportunitiesListOptions = (query: OpportunitiesListFilters) =>
  queryOptions({ queryKey: qk.opportunities.list(query), queryFn: () => listOpportunities(query) });

/** Liste minimale des utilisateurs (`GET /users`), filtre owner et assignation. */
export const usersQueryOptions = () =>
  queryOptions({
    queryKey: qk.users,
    queryFn: async () => (await call(api.users.$get())).items,
    staleTime: 300_000,
  });

export const bulkStatus = (json: BulkStatusBody) =>
  call(api.opportunities['bulk-status'].$post({ json }));

export const bulkAssign = (json: BulkAssignBody) =>
  call(api.opportunities['bulk-assign'].$post({ json }));

export const createOpportunity = (json: ManualCreateInput) =>
  call(api.opportunities.$post({ json }));

/** Recherche de cible de fusion : opportunités du marché, par score revu décroissant. */
export const mergeCandidatesQuery = (marketId: string, q: string): OpportunitiesListFilters => ({
  page: '1',
  pageSize: String(MERGE_SEARCH_PAGE_SIZE),
  sort: 'reviewedAttractiveness',
  order: 'desc',
  marketId,
  ...(q ? { q } : {}),
});
